import { useState, useContext } from "react";
import DeviceOrientationContext from "../context/DeviceOrientationContext";
import {
  StyleSheet,
  ScrollView,
  View,
  Text,
  TouchableOpacity,
  Image,
  Platform,
} from "react-native";

import oktaConfig from "../../configs/okta";
import { useDispatch, useSelector } from "react-redux";
import { SimpleLineIcons } from "@expo/vector-icons";
import { persistor } from "../../redux/configureStore";
import { logout } from "../../redux/reducers/adminReducer";
import { useTranslation } from "../../utils/useTranslation";



function AdminCustomDrawerContent(props: any) {
  const [mainDrawer, setMainDrawer] = useState(true);
  const [filteredItems, setFilteredItems] = useState<any>([]);
  const { t } = useTranslation();
  const { isMobileDevice, isSmallDevice } = useContext(
    DeviceOrientationContext
  );
  const admin = useSelector((state: any) => state.admin);
  const dispatch = useDispatch<any>();

  const toggleMainDrawer = () => {
    setMainDrawer(true);
    setFilteredItems([]);
  };

  const logoutHandler = () => {
    dispatch(logout());
    persistor.purge();
    window.location.href = `${oktaConfig.api.redirectUri}/admin/login`;
  };

  const onItemParentPress = (key: any) => {
    const filteredMainDrawerRoutes = props.drawerItems.find((e: any) => {
      return e && e.key === key;
    });
    if (filteredMainDrawerRoutes.routes.length === 1) {
      const selectedRoute = filteredMainDrawerRoutes.routes[0];
      props.navigation.toggleDrawer();
      props.navigation.navigate(selectedRoute.nav, {
        screen: selectedRoute.routeName,
      });
    } else {
      setMainDrawer(false);
      setFilteredItems(filteredMainDrawerRoutes);
    }
  };

  function renderMainDrawer() {
    return (
      <View style={styles.menuList}>
        {props.drawerItems.map(
          (parent: any) =>
            parent && (
              <TouchableOpacity
                key={parent.key}
                testID={parent.key}
                activeOpacity={0.7}
                onPress={() => onItemParentPress(parent.key)}
                style={styles.menuItem}
              >
                <Text style={styles.menuText}>{parent.title}</Text>
                {parent.routes && parent.routes.length > 1 && (
                  <Text style={styles.chevronText}>›</Text>
                )}
              </TouchableOpacity>
            )
        )}
        {renderLogoutBtn()}
      </View>
    );
  }

  function renderFilteredItemsDrawer() {
    return (
      <View style={styles.menuList}>
        <TouchableOpacity
          onPress={() => toggleMainDrawer()}
          style={styles.backButton}
          activeOpacity={0.7}
        >
          <Text style={styles.backArrow}>‹</Text>
          <Text style={styles.backButtonLabel}>{t("back") || "Back"}</Text>
        </TouchableOpacity>

        <View style={styles.divider} />

        {filteredItems.routes.map((route: any) => (
          <TouchableOpacity
            key={route.routeName}
            testID={route.routeName}
            activeOpacity={0.7}
            onPress={() =>
              props.navigation.navigate(route.nav, {
                screen: route.routeName,
              })
            }
            style={styles.menuItem}
          >
            <Text style={styles.menuText}>{route.title}</Text>
          </TouchableOpacity>
        ))}
      </View>
    );
  }

  function renderLogoutBtn() {
    if (Platform.OS === "web") return null;
    return (
      <>
        <View style={styles.divider} />
        <TouchableOpacity
          onPress={() => logoutHandler()}
          testID="customDrawer-logout"
          activeOpacity={0.7}
          style={styles.menuItem}
        >
          <Text style={[styles.menuText, { color: "#EF4444" }]}>Log out</Text>
        </TouchableOpacity>
      </>
    );
  }

  function renderBottomSettings() {
    return (
      <View style={styles.accountSection}>
        <View style={styles.accountRow}>
          <View style={styles.avatarContainer}>
            <View style={styles.adminAvatar}>
              <Text style={styles.adminAvatarText}>
                {admin?.fullName?.charAt(0) || "A"}
              </Text>
            </View>
          </View>
          <View style={{ flex: 1 }}>
            <Text style={styles.accountUserText} numberOfLines={1}>
              {admin?.fullName || "Admin"}
            </Text>
          </View>
          <TouchableOpacity
            style={styles.settingsButton}
            onPress={() => props.navigation.navigate("SettingsM")}
            activeOpacity={0.7}
          >
            <SimpleLineIcons name="options" size={14} color="#64748B" />
          </TouchableOpacity>
        </View>
      </View>
    );
  }

  return (
    <View style={styles.drawerContainer}>
      <ScrollView
        style={styles.scrollArea}
        contentContainerStyle={styles.scrollContent}
        showsVerticalScrollIndicator={false}
      >

        {/* Navigation items */}
        {mainDrawer ? renderMainDrawer() : renderFilteredItemsDrawer()}
      </ScrollView>

      {/* Bottom user profile (mobile only) */}
      {(isMobileDevice || isSmallDevice) && renderBottomSettings()}
    </View>
  );
}

const styles = StyleSheet.create({
  drawerContainer: {
    flex: 1,
    backgroundColor: "#FFFFFF",
  },
  scrollArea: {
    flex: 1,
  },
  scrollContent: {
    paddingBottom: 20,
  },
  logoContainer: {
    alignItems: "center",
    paddingVertical: 20,
  },
  logo: {
    width: 72,
    height: 72,
  },
  divider: {
    height: 1,
    backgroundColor: "#F1F5F9",
    marginHorizontal: 16,
  },

  // Menu items
  menuList: {
    paddingTop: 16,
    paddingHorizontal: 10,
  },
  menuItem: {
    flexDirection: "row",
    alignItems: "center",
    paddingVertical: 14,
    paddingHorizontal: 10,
    borderRadius: 8,
    marginBottom: 8,
  },
  menuText: {
    flex: 1,
    fontSize: 13,
    fontWeight: "600",
    color: "#334155",
  },
  chevronText: {
    fontSize: 18,
    color: "#94A3B8",
    marginLeft: 4,
  },

  // Back button (sub-drawer)
  backButton: {
    flexDirection: "row",
    alignItems: "center",
    paddingVertical: 10,
    paddingHorizontal: 10,
    marginTop: 0,
    marginBottom: 8,
  },
  backArrow: {
    fontSize: 20,
    color: "#3B82F6",
    fontWeight: "600",
  },
  backButtonLabel: {
    fontSize: 13,
    fontWeight: "600",
    color: "#3B82F6",
    marginLeft: 4,
  },

  // Bottom account section
  accountSection: {
    borderTopWidth: 1,
    borderTopColor: "#F1F5F9",
    paddingVertical: 10,
    paddingHorizontal: 12,
  },
  accountRow: {
    flexDirection: "row",
    alignItems: "center",
  },
  avatarContainer: {
    marginRight: 10,
  },
  adminAvatar: {
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: "#3B82F6",
    justifyContent: "center",
    alignItems: "center",
  },
  adminAvatarText: {
    color: "#FFFFFF",
    fontSize: 14,
    fontWeight: "700",
  },
  accountUserText: {
    fontSize: 13,
    fontWeight: "600",
    color: "#334155",
  },
  settingsButton: {
    padding: 8,
    borderRadius: 6,
    backgroundColor: "#F8FAFC",
  },
});

export default AdminCustomDrawerContent;
